import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { MdRefresh, MdLightbulbOutline } from 'react-icons/md';
import cx from 'classnames';

const ConversationStarter = ({
  variants = [],
  isLoading = false,
  refreshCount = 0,
  onRefresh = () => {},
  onSelect = () => {},
  onDismiss = () => {},
  fetchStarter = () => {},
}) => {
  const intl = useIntl();
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  // Automatically pre-fill the chat input when the first suggestion loads
  useEffect(() => {
    if (variants.length === 1 && !isLoading) {
      onSelect(variants[0].message);
    }
  }, [variants.length, isLoading, onSelect]);

  const handleHeaderClick = (e) => {
    e.preventDefault();
    if (variants.length === 0) {
      fetchStarter();
    } else if (variants.length === 1) {
      onRefresh();
    }
  };

  const handleOptionSelect = (index) => {
    setSelectedOptionIndex(index);
    onSelect(variants[index].message);
  };

  const isBtnDisabled = isLoading || variants.length >= 2;

  const renderIcon = () => {
    if (variants.length === 0 && !isLoading) {
      return <MdLightbulbOutline aria-hidden="true" />;
    }
    return (
      <MdRefresh
        className={cx('c-conversation-starter__refresh-icon', {
          'c-conversation-starter__refresh-icon--spinning': isLoading,
        })}
        aria-hidden="true"
      />
    );
  };

  return (
    <div className="c-conversation-starter">
      <div className="c-conversation-starter__header">
        <button
          type="button"
          className={cx('c-conversation-starter__header-btn', {
            'c-conversation-starter__header-btn--disabled': isBtnDisabled,
          })}
          onClick={handleHeaderClick}
          disabled={isBtnDisabled}
          aria-label={
            variants.length === 0
              ? intl.formatMessage({
                  id: 'components.chat.conversation-starter.title',
                  defaultMessage: 'Suggested openers',
                })
              : intl.formatMessage({
                  id: 'components.chat.conversation-starter.limit-info',
                  defaultMessage: 'You can generate up to 2 suggestions',
                })
          }
        >
          <span className="c-conversation-starter__header-icon">
            {renderIcon()}
          </span>
          <span className="c-conversation-starter__header-text">
            {variants.length === 0
              ? intl.formatMessage({
                  id: 'components.chat.conversation-starter.title',
                  defaultMessage: 'Suggested openers',
                })
              : intl.formatMessage({
                  id: 'components.chat.conversation-starter.limit-info',
                  defaultMessage: 'You can generate up to 2 suggestions',
                })}
          </span>
        </button>
      </div>

      {isLoading && (
        <div className="c-conversation-starter__skeleton-list">
          <div className="c-conversation-starter__skeleton" />
        </div>
      )}

      {variants.length === 2 && (
        <div className="c-conversation-starter__body">
          <div className="c-conversation-starter__choose-title">
            {intl.formatMessage({
              id: 'components.chat.conversation-starter.choose-version',
              defaultMessage: 'Choose the version you like',
            })}
          </div>

          <div className="c-conversation-starter__options-container">
            {variants.map((item, index) => {
              const isSelected = selectedOptionIndex === index;
              return (
                <div
                  key={item.request_id || index}
                  className={cx('c-conversation-starter__option', {
                    'c-conversation-starter__option--selected': isSelected,
                  })}
                  onClick={() => handleOptionSelect(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleOptionSelect(index);
                    }
                  }}
                >
                  <div className="c-conversation-starter__radio-container">
                    <span
                      className={cx('c-conversation-starter__radio-circle', {
                        'c-conversation-starter__radio-circle--selected': isSelected,
                      })}
                    />
                  </div>
                  <div className="c-conversation-starter__option-content">
                    <h4 className="c-conversation-starter__option-title">
                      {intl.formatMessage(
                        {
                          id: 'components.chat.conversation-starter.suggestion-title',
                          defaultMessage: 'Suggestion {number}',
                        },
                        { number: index + 1 }
                      )}
                    </h4>
                    <p className="c-conversation-starter__option-text">
                      {item.message}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="c-conversation-starter__dismiss-all">
            <button
              type="button"
              className="c-btn-starter-dismiss"
              onClick={onDismiss}
            >
              {intl.formatMessage({
                id: 'components.chat.conversation-starter.no-thanks',
                defaultMessage: 'No, thanks',
              })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationStarter;
